/**
 * UI mirror of medplum-server/src/bots/treatment-plan/evidence-grounded-symptoms.ts
 * Keep types and helpers in sync.
 */

import { splitDiagnosisCodeAndDescription } from '../treatmentPlanFormHelpers';

const ICD_10_CM_SYSTEM = 'http://hl7.org/fhir/sid/icd-10-cm';

export type SymptomReportedBy = 'client' | 'provider';

export type SymptomSourceType =
  | 'guided-answer'
  | 'treatment-plan-ui'
  | 'intake-transcript'
  | 'intake-form'
  | 'signed-note';

export interface EvidenceGroundedSymptomCoding {
  system?: string;
  code?: string;
  display?: string;
}

export interface EvidenceGroundedSymptomAttributes {
  severity?: string;
  frequency?: string;
  duration?: string;
  onset?: string;
  functionalImpact?: string;
}

export interface EvidenceGroundedSymptom {
  id: string;
  label: string;
  normalizedCode?: EvidenceGroundedSymptomCoding;
  reportedBy: SymptomReportedBy;
  sourceType: SymptomSourceType;
  sourceReference?: string;
  confirmedByProvider: boolean;
  attributes?: EvidenceGroundedSymptomAttributes;
}

export type TreatmentPlanOperation = 'initial' | 'revision' | 'renewal' | 'refresh';

export interface TreatmentPlanClinicalContextDiagnosis {
  system: string;
  code: string;
  display: string;
  conditionReference?: string;
  primary?: boolean;
}

export interface TreatmentPlanClinicalContext {
  patientReference: string;
  episodeOfCareReference?: string;
  diagnoses: TreatmentPlanClinicalContextDiagnosis[];
  symptoms: EvidenceGroundedSymptom[];
  operation: TreatmentPlanOperation;
}

export function normalizeSymptomPhrase(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Builds AI clinical-context diagnoses from raw `"CODE - description"` values
 * already stored on the treatment-plan draft (e.g. `ParsedTreatmentPlanDiagnosis.code`).
 * Skips entries with no parseable code. The first entry is marked primary.
 */
export function diagnosesFromTreatmentPlanDraftCodes(
  codes: Array<string | undefined>,
): TreatmentPlanClinicalContextDiagnosis[] {
  return codes
    .map((raw) => raw?.trim())
    .filter((raw): raw is string => Boolean(raw))
    .map((raw) => splitDiagnosisCodeAndDescription(raw))
    .filter((parsed) => Boolean(parsed.code))
    .map((parsed, index) => ({
      system: ICD_10_CM_SYSTEM,
      code: parsed.code,
      display: parsed.description || parsed.code,
      primary: index === 0,
    }));
}

export function symptomsFromTreatmentPlanUiTexts(
  texts: Array<string | undefined>,
): EvidenceGroundedSymptom[] {
  return texts
    .map((text) => text?.trim())
    .filter((text): text is string => Boolean(text))
    .map((label, index) => ({
      id: `tp-ui-${index}`,
      label,
      reportedBy: 'provider' as const,
      sourceType: 'treatment-plan-ui' as const,
      confirmedByProvider: true,
    }));
}

/**
 * Convert checklist-selected symptom labels into provider-confirmed guided-answer evidence.
 * Custom "Other" text is included when present.
 */
export function symptomsFromGuidedChecklistLabels(
  labels: string[],
  opts?: { checklistId?: string; reportedBy?: SymptomReportedBy },
): EvidenceGroundedSymptom[] {
  const reportedBy = opts?.reportedBy ?? 'provider';
  const prefix = opts?.checklistId ? `guided-${opts.checklistId}` : 'guided';
  return labels
    .map((label) => label.trim())
    .filter(Boolean)
    .map((label, index) => ({
      id: `${prefix}-${index}-${normalizeSymptomPhrase(label).slice(0, 24) || index}`,
      label,
      reportedBy,
      sourceType: 'guided-answer' as const,
      confirmedByProvider: true,
    }));
}

/** Merge form + guided symptoms; first occurrence of a normalized phrase wins. */
export function mergeEvidenceGroundedSymptoms(
  ...lists: Array<EvidenceGroundedSymptom[] | undefined>
): EvidenceGroundedSymptom[] {
  const seen = new Set<string>();
  const merged: EvidenceGroundedSymptom[] = [];
  for (const list of lists) {
    for (const symptom of list ?? []) {
      const key = normalizeSymptomPhrase(symptom.label);
      if (!key || seen.has(key)) continue;
      seen.add(key);
      merged.push(symptom);
    }
  }
  return merged;
}

/**
 * Display labels for persisted behavioral-definition linkIds.
 * Stable FHIR suffixes remain `-behavioral-definition` / `-behavioral-definition-text`.
 */
export const SYMPTOM_DISPLAY = {
  sectionTitle: 'Symptoms',
  singular: 'Symptom',
  plural: 'Symptoms',
  addButton: 'Add symptom',
  placeholder: 'Enter a client-reported or provider-confirmed symptom…',
  missingQualityMessage: 'No symptom is documented.',
} as const;

/** Map Questionnaire item display text that still says Behavioral Definition(s). */
export function displayLabelForTreatmentPlanItem(text: string | undefined): string | undefined {
  if (!text) return text;
  const trimmed = text.trim();
  if (/^behavioral definitions?$/i.test(trimmed)) {
    return trimmed.toLowerCase().endsWith('s') ? SYMPTOM_DISPLAY.plural : SYMPTOM_DISPLAY.singular;
  }
  if (/^behavioral definition$/i.test(trimmed)) {
    return SYMPTOM_DISPLAY.singular;
  }
  return text;
}

/**
 * Compatibility reader: prefer `symptoms` on a parsed object when present,
 * otherwise fall back to legacy `behavioralDefinitions`.
 */
export function readSymptomsCompat<T extends { text?: string }>(input: {
  symptoms?: T[];
  behavioralDefinitions?: T[];
}): T[] {
  if (input.symptoms && input.symptoms.length > 0) return input.symptoms;
  return input.behavioralDefinitions ?? [];
}
